import {createContext, use, useMemo, useState} from 'react'

interface FileQueryContextType {
  query: string
  setQuery: (query: string) => void
}

export const FileQueryContext = createContext<FileQueryContextType>({query: '', setQuery: () => undefined})

/**
 * Context exposing only the query setter.
 * The setter returned by useState is referentially stable, so dispatch-only consumers never re-render — notably
 * not on every keystroke as the query updates. State readers should keep using useFileQueryContext().
 */
const FileQueryDispatchContext = createContext<(query: string) => void>(() => undefined)

/**
 * Hook to read both the query and its setter.
 * Re-renders whenever the query changes. Use useSetFileQuery() if you only need to set the query.
 */
export function useFileQueryContext() {
  return use(FileQueryContext)
}

/**
 * Hook to get the query setter without subscribing to query changes.
 * Never re-renders due to query updates, since the setter identity is stable.
 */
export function useSetFileQuery(): (query: string) => void {
  return use(FileQueryDispatchContext)
}

export function FileQueryProvider({children}: {children: React.ReactNode}) {
  const [query, setQuery] = useState('')
  const value = useMemo(() => ({query, setQuery}), [setQuery, query])

  return (
    <FileQueryContext value={value}>
      <FileQueryDispatchContext value={setQuery}>{children}</FileQueryDispatchContext>
    </FileQueryContext>
  )
}
